import React, { useState } from 'react';
import { FileText, Calendar, User, Globe, ChevronDown, ChevronUp, Monitor, Eye, EyeOff } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { formatDate } from '../lib/utils';

// Sensitive field names that should be masked
const SENSITIVE_FIELDS = [
    'api_key',
    'secret',
    'password',
    'token',
    'webhook_secret',
    'private_key',
    'client_secret',
    'merchant_key',
    'access_token',
    'refresh_token',
    'authorization',
    'credentials',
];

// Recursively mask sensitive data in objects
const maskSensitiveData = (obj: any, showSensitive: boolean = false): any => {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => maskSensitiveData(item, showSensitive));
    }

    if (typeof obj === 'object') {
        const masked: any = {};
        for (const [key, value] of Object.entries(obj)) {
            const keyLower = key.toLowerCase();
            const isSensitive = SENSITIVE_FIELDS.some(field => keyLower.includes(field));

            if (isSensitive && !showSensitive) {
                // Mask the value
                if (typeof value === 'string' && value.length > 0) {
                    masked[key] = '••••••••' + (value.length > 8 ? value.slice(-4) : '');
                } else {
                    masked[key] = '••••••••';
                }
            } else if (typeof value === 'object' && value !== null) {
                // Recursively mask nested objects
                masked[key] = maskSensitiveData(value, showSensitive);
            } else {
                masked[key] = value;
            }
        }
        return masked;
    }

    return obj;
};

const AuditLogs: React.FC = () => {
    const [page, setPage] = useState(1);
    const [action, setAction] = useState('');
    const [resourceType, setResourceType] = useState('');
    const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
    const [showSensitiveData, setShowSensitiveData] = useState<Set<number>>(new Set());

    const { data, isLoading } = useAuditLogs({
        page,
        per_page: 20,
        action: action || undefined,
        resource_type: resourceType || undefined,
    });

    const toggleLog = (logId: number) => {
        setExpandedLogs((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(logId)) {
                newSet.delete(logId);
            } else {
                newSet.add(logId);
            }
            return newSet;
        });
    };

    const toggleSensitiveData = (logId: number) => {
        setShowSensitiveData((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(logId)) {
                newSet.delete(logId);
            } else {
                newSet.add(logId);
            }
            return newSet;
        });
    };

    const getActionColor = (actionName: string): string => {
        if (actionName.includes('created') || actionName.includes('invited')) return 'text-green-700 bg-green-100';
        if (actionName.includes('deleted') || actionName.includes('removed')) return 'text-red-700 bg-red-100';
        if (actionName.includes('updated') || actionName.includes('rotated')) return 'text-blue-700 bg-blue-100';
        if (actionName.includes('resolved')) return 'text-purple-700 bg-purple-100';
        return 'text-gray-700 bg-gray-100';
    };

    const getActionIcon = (actionName: string) => {
        if (actionName.includes('api_key')) return '🔑';
        if (actionName.includes('team') || actionName.includes('invitation') || actionName.includes('invited')) return '👥';
        if (actionName.includes('organization')) return '🏢';
        if (actionName.includes('provider')) return '🔌';
        if (actionName.includes('alert') || actionName.includes('threshold')) return '⚠️';
        if (actionName.includes('risk')) return '🚨';
        if (actionName.includes('report')) return '📄';
        if (actionName.includes('settings')) return '⚙️';
        return '📝';
    };

    const getBrowserInfo = (userAgent: string) => {
        if (userAgent.includes('Chrome')) return '🌐 Chrome';
        if (userAgent.includes('Firefox')) return '🦊 Firefox';
        if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return '🧭 Safari';
        if (userAgent.includes('Edge')) return '🌊 Edge';
        return '💻 Browser';
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading audit logs..." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
                <p className="text-gray-600 mt-1">Track all actions performed in your organization</p>
            </div>

            {/* Filters */}
            <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                        <select
                            value={action}
                            onChange={(e) => {
                                setAction(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All Actions</option>
                            <option value="api_key_created">API Key Created</option>
                            <option value="api_key_deleted">API Key Deleted</option>
                            <option value="api_key_rotated">API Key Rotated</option>
                            <option value="team_member_invited">Member Invited</option>
                            <option value="team_member_removed">Member Removed</option>
                            <option value="organization_updated">Organization Updated</option>
                            <option value="provider_updated">Provider Updated</option>
                            <option value="risk_resolved">Risk Resolved</option>
                            <option value="report_deleted">Report Deleted</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                        <select
                            value={resourceType}
                            onChange={(e) => {
                                setResourceType(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All Resources</option>
                            <option value="api_key">API Key</option>
                            <option value="team_member">Team Member</option>
                            <option value="organization">Organization</option>
                            <option value="provider_config">Provider Config</option>
                            <option value="alert_config">Alert Config</option>
                            <option value="risk_flag">Risk Flag</option>
                            <option value="report">Report</option>
                        </select>
                    </div>
                </div>
            </Card>

            {/* Logs List */}
            <Card>
                {!data || !data.data || data.data.length === 0 ? (
                    <EmptyState
                        icon={FileText}
                        title="No audit logs found"
                        description="No actions have been performed yet, or try adjusting your filters"
                    />
                ) : (
                    <div className="space-y-3">
                        {data.data.map((log) => {
                            const isExpanded = expandedLogs.has(log.id);
                            const hasDetails = log.old_values || log.new_values || log.metadata;

                            return (
                                <div
                                    key={log.id}
                                    className="border border-gray-200 rounded-lg hover:border-gray-300 transition-colors overflow-hidden"
                                >
                                    {/* Main Content */}
                                    <div className="p-4">
                                        <div className="flex items-start gap-3">
                                            {/* Icon */}
                                            <div className="text-2xl flex-shrink-0">{getActionIcon(log.action)}</div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                {/* Action Label */}
                                                <p className="text-sm font-semibold text-gray-900 mb-2">
                                                    {log.action_label}
                                                </p>

                                                {/* Meta Info */}
                                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                                    {/* User */}
                                                    <div className="flex items-center gap-1.5">
                                                        <User className="w-3.5 h-3.5" />
                                                        <span className="font-medium">{log.user?.name || 'Unknown'}</span>
                                                        {log.user?.email && (
                                                            <span className="text-gray-500">({log.user.email})</span>
                                                        )}
                                                    </div>

                                                    {/* Date */}
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar className="w-3.5 h-3.5" />
                                                        <span>{formatDate(log.performed_at)}</span>
                                                    </div>

                                                    {/* IP Address */}
                                                    {log.ip_address && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Globe className="w-3.5 h-3.5" />
                                                            <span className="font-mono">{log.ip_address}</span>
                                                        </div>
                                                    )}

                                                    {/* Browser */}
                                                    {log.user_agent && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Monitor className="w-3.5 h-3.5" />
                                                            <span>{getBrowserInfo(log.user_agent)}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Badge & Entity Type */}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <Badge className={`text-xs ${getActionColor(log.action)}`}>
                                                        {log.action.replace(/_/g, ' ')}
                                                    </Badge>
                                                    {log.entity_type && (
                                                        <span className="text-xs text-gray-500 capitalize">
                                                            {log.entity_type.replace(/_/g, ' ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Expand Button */}
                                            {hasDetails && (
                                                <button
                                                    onClick={() => toggleLog(log.id)}
                                                    className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                    title={isExpanded ? 'Hide details' : 'Show details'}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronUp className="w-5 h-5 text-gray-600" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5 text-gray-600" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    {isExpanded && hasDetails && (
                                        <div className="px-4 pb-4 space-y-3">
                                            {/* Show/Hide Sensitive Data Toggle */}
                                            {(log.old_values || log.new_values) && (
                                                <div className="flex items-center justify-end">
                                                    <button
                                                        onClick={() => toggleSensitiveData(log.id)}
                                                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                        title={showSensitiveData.has(log.id) ? 'Hide sensitive data' : 'Show sensitive data'}
                                                    >
                                                        {showSensitiveData.has(log.id) ? (
                                                            <>
                                                                <EyeOff className="w-3.5 h-3.5" />
                                                                Hide Sensitive Data
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Eye className="w-3.5 h-3.5" />
                                                                Show Sensitive Data
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}

                                            {/* Old Values */}
                                            {log.old_values && Object.keys(log.old_values).length > 0 && (
                                                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                                    <h4 className="text-xs font-semibold text-red-900 mb-2">Previous Values</h4>
                                                    <pre className="text-xs text-red-800 overflow-x-auto">
                                                        {JSON.stringify(
                                                            maskSensitiveData(log.old_values, showSensitiveData.has(log.id)),
                                                            null,
                                                            2
                                                        )}
                                                    </pre>
                                                </div>
                                            )}

                                            {/* New Values */}
                                            {log.new_values && Object.keys(log.new_values).length > 0 && (
                                                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                                                    <h4 className="text-xs font-semibold text-green-900 mb-2">New Values</h4>
                                                    <pre className="text-xs text-green-800 overflow-x-auto">
                                                        {JSON.stringify(
                                                            maskSensitiveData(log.new_values, showSensitiveData.has(log.id)),
                                                            null,
                                                            2
                                                        )}
                                                    </pre>
                                                </div>
                                            )}

                                            {/* Metadata */}
                                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                                    <h4 className="text-xs font-semibold text-blue-900 mb-2">Additional Information</h4>
                                                    <pre className="text-xs text-blue-800 overflow-x-auto">
                                                        {JSON.stringify(log.metadata, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Pagination */}
            {data && data.meta && data.meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">
                        Page {page} of {data.meta.last_page}
                    </span>
                    <button
                        onClick={() => setPage((p) => Math.min(data.meta.last_page, p + 1))}
                        disabled={page === data.meta.last_page}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default AuditLogs;
